import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Modal, Form, Input, message, Popconfirm, Space, Card
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ShopOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Customer, CreateCustomer } from '../types';
import { customerApi } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const CustomerPage: React.FC = () => {
  const { t } = useLanguage();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [form] = Form.useForm();

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await customerApi.getAll();
      setCustomers(response.data);
    } catch (error) {
      message.error(t('failed.to.fetch.customers') || 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleAdd = () => {
    setEditingCustomer(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    form.setFieldsValue(customer);
    setModalVisible(true);
  };

  const handleSubmit = async (values: CreateCustomer) => {
    try {
      if (editingCustomer) {
        await customerApi.update(editingCustomer.id, values);
        message.success(t('customer.updated.successfully') || 'Customer updated successfully');
      } else {
        await customerApi.create(values);
        message.success(t('customer.created.successfully') || 'Customer created successfully');
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchCustomers();
    } catch (error) {
      message.error(editingCustomer 
        ? (t('failed.to.update.customer') || 'Failed to update customer')
        : (t('failed.to.create.customer') || 'Failed to create customer')
      );
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await customerApi.delete(id);
      message.success(t('customer.deleted.successfully') || 'Customer deleted successfully');
      fetchCustomers();
    } catch (error) {
      message.error(t('failed.to.delete.customer') || 'Failed to delete customer');
    }
  };

  const columns: ColumnsType<Customer> = [
    {
      title: t('name') || 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: t('phone') || 'Phone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: t('email') || 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('company.name') || 'Company',
      dataIndex: 'companyName',
      key: 'companyName',
    },
    {
      title: t('address') || 'Address',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
    },
    {
      title: t('created.by') || 'Created By',
      dataIndex: 'createdByUserName',
      key: 'createdByUserName',
    },
    {
      title: t('actions') || 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title={t('delete.customer.confirm') || 'Are you sure you want to delete this customer?'}
            onConfirm={() => handleDelete(record.id)}
            okText={t('yes') || 'Yes'}
            cancelText={t('no') || 'No'}
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading && customers.length === 0) {
    return <div className="container-fluid mt-4"><div className="spinner-border"></div></div>;
  }

  return (
    <div className="container-fluid mt-4">
      <Card>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">
            <ShopOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            {t('customer.management') || 'Customer Management'}
          </h2>
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            {t('add.customer') || 'Add Customer'}
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={customers}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />

        <Modal
          title={editingCustomer ? (t('edit.customer') || 'Edit Customer') : (t('add.customer') || 'Add Customer')}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          width={800}
        >
          <Form
            form={form}
            layout="horizontal"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            onFinish={handleSubmit}
          >
            <Form.Item
              label={t('name') || 'Name'}
              name="name"
              rules={[{ required: true, message: t('required.field') || 'This field is required' }]}
            >
              <Input maxLength={200} />
            </Form.Item>

            <Form.Item
              label={t('phone') || 'Phone'}
              name="phone"
              rules={[{ required: true, message: t('required.field') || 'This field is required' }]}
            >
              <Input maxLength={100} />
            </Form.Item>

            <Form.Item
              label={t('email') || 'Email'}
              name="email"
              rules={[
                { type: 'email', message: t('invalid.email') || 'Invalid email format' }
              ]}
            >
              <Input maxLength={200} />
            </Form.Item>

            <Form.Item
              label={t('company.name') || 'Company Name'}
              name="companyName"
            >
              <Input maxLength={200} />
            </Form.Item>

            <Form.Item
              label={t('address') || 'Address'}
              name="address"
            >
              <Input.TextArea maxLength={500} rows={3} />
            </Form.Item>

            <Form.Item
              label={t('notes') || 'Notes'}
              name="notes"
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default CustomerPage;